import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, OutlineCard, useToast } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  fetchScandinavianKeywordFormSchema,
  fetchUserProfileData,
  IUserDetailsResponse,
  useUpdatedUserInfoMutation
} from 'src/api/signUpMutation';
import { Layout } from 'src/components/Layout';
import { Overlay } from 'src/components/Overlay';
import { SignUpFormFields } from '../signup-form-fields';
import {
  buildRowDuplicateMessage,
  findRowLevelDuplicates,
  IUserDetailsStepThreeForm
} from './signupHook';
import SpecialtyKeywordTable, {
  SpecialtyKeywordRow
} from './SpecialtyKeywordTable';

const mapProfileToFormValues = (
  data?: IUserDetailsResponse
): IUserDetailsStepThreeForm => ({
  researchFocusAndExpertise: {
    reviewerInterest: data?.researchFocusAndExpertise?.reviewerInterest ?? '',
    unavailabilityPeriodFrom:
      data?.researchFocusAndExpertise?.unavailabilityPeriodFrom ?? '',
    unavailabilityPeriodTo:
      data?.researchFocusAndExpertise?.unavailabilityPeriodTo ?? '',
    specialtyAndKeywords:
      data?.researchFocusAndExpertise?.specialtyAndKeywords?.map(item => ({
        specialty: item.specialtyId
          ? {
              termId: item.specialtyId,
              name: item.specialtyName ?? ''
            }
          : null,
        subSpecialty: item.subSpecialtyId
          ? {
              termId: item.subSpecialtyId,
              name: item.subSpecialtyName ?? ''
            }
          : null,
        keyword: item.keywordId
          ? {
              termId: item.keywordId,
              name: item.keyword ?? ''
            }
          : null,
        scandinavianKeyword: item.scandinavianKeywordId
          ? {
              termId: item.scandinavianKeywordId,
              name: item.scandinavianKeyword ?? ''
            }
          : null,
        isCustomKeyword: item.keywordId?.startsWith('custom_') ?? false
      })) ?? [],
    scandinavianKeywords:
      data?.researchFocusAndExpertise?.scandinavianKeywords ?? [],
    scandinavianKeywordsTemp:
      data?.researchFocusAndExpertise?.scandinavianKeywords?.map(value => ({
        text: value
      })) ?? [],
    professionalInterests:
      data?.researchFocusAndExpertise?.professionalInterests ?? [],
    professionalInterestsTemp:
      data?.researchFocusAndExpertise?.professionalInterests?.map(value => ({
        text: value
      })) ?? []
  }
});

const buildResearchFocusPayload = (
  values: IUserDetailsStepThreeForm,
  existingData?: IUserDetailsResponse
) => {
  const researchFocusAndExpertise = values.researchFocusAndExpertise;
  const filteredSpecialtyAndKeywords =
    researchFocusAndExpertise.specialtyAndKeywords?.filter(
      (item: SpecialtyKeywordRow) =>
        item.specialty ||
        item.subSpecialty ||
        item.keyword ||
        item.scandinavianKeyword
    ) ?? [];
  const reviewerInterest =
    researchFocusAndExpertise.reviewerInterest ||
    existingData?.researchFocusAndExpertise?.reviewerInterest ||
    '';

  return {
    section: 'RESEARCH_FOCUS_AND_EXPERTISE',
    researchFocusAndExpertise: {
      reviewerInterest,
      unavailabilityPeriodFrom:
        reviewerInterest === 'Yes'
          ? researchFocusAndExpertise.unavailabilityPeriodFrom ||
            existingData?.researchFocusAndExpertise?.unavailabilityPeriodFrom ||
            ''
          : '',
      unavailabilityPeriodTo:
        reviewerInterest === 'Yes'
          ? researchFocusAndExpertise.unavailabilityPeriodTo ||
            existingData?.researchFocusAndExpertise?.unavailabilityPeriodTo ||
            ''
          : '',
      specialtyAndKeywords: filteredSpecialtyAndKeywords.map(item => ({
        specialtyName: item.specialty?.name ?? '',
        specialtyId: item.specialty?.termId ?? '',
        subSpecialtyName: item.subSpecialty?.name ?? '',
        subSpecialtyId: item.subSpecialty?.termId ?? '',
        keyword: item.keyword?.name ?? '',
        keywordId: item.keyword?.termId ?? '',
        scandinavianKeyword: item.scandinavianKeyword?.name ?? '',
        scandinavianKeywordId: item.scandinavianKeyword?.termId ?? ''
      })),
      scandinavianKeywords:
        researchFocusAndExpertise.scandinavianKeywords ??
        existingData?.researchFocusAndExpertise?.scandinavianKeywords ??
        []
    }
  };
};

const getSchemaDocument = (schemaData: any) =>
  schemaData?.documentSchema ?? schemaData?.data?.documentSchema;

const ScandinavianKeywordProfileOverlay = ({
  isOpen,
  submissionContextId,
  onClose,
  onSaved
}: {
  isOpen: boolean;
  submissionContextId: string;
  onClose: () => void;
  onSaved?: () => void | Promise<void>;
}) => {
  const toast = useToast();
  const updatedMutation = useUpdatedUserInfoMutation();
  const methods = useForm<IUserDetailsStepThreeForm>({
    mode: 'onChange',
    reValidateMode: 'onBlur'
  });

  const { handleSubmit, reset, getValues, watch, trigger } = methods;

  const { data: profileResponse, isLoading: isProfileLoading } = useQuery({
    queryKey: ['scandinavian-keyword-overlay-profile-details'],
    queryFn: fetchUserProfileData,
    enabled: isOpen
  });

  const { data: schemaData, isLoading: isSchemaLoading } = useQuery({
    queryKey: [
      'submission-context',
      submissionContextId,
      'scandinavian-keyword-form-schema'
    ],
    queryFn: () =>
      submissionContextId
        ? fetchScandinavianKeywordFormSchema(submissionContextId)
        : null,
    enabled: isOpen && Boolean(submissionContextId)
  });

  const userDetails = profileResponse?.data;
  const documentSchema = getSchemaDocument(schemaData);

  const configs = useMemo(() => {
    if (!documentSchema) return null;

    const signupFormFields = new SignUpFormFields(documentSchema);

    return {
      Specialty: signupFormFields.getFieldConfig('Specialty'),
      SubSpecialty: signupFormFields.getFieldConfig('SubSpecialty'),
      Keyword: signupFormFields.getFieldConfig('Keyword'),
      ScandinavianKeyword: signupFormFields.getFieldConfig(
        'ScandinavianKeyword'
      )
    };
  }, [documentSchema]);

  useEffect(() => {
    if (!isOpen || !userDetails) return;

    reset(mapProfileToFormValues(userDetails));
  }, [isOpen, reset, userDetails]);

  const handleSave = async (values: IUserDetailsStepThreeForm) => {
    const isValid = await trigger();
    if (!isValid) return;

    const liveData =
      getValues('researchFocusAndExpertise.specialtyAndKeywords') ||
      watch('researchFocusAndExpertise.specialtyAndKeywords') ||
      [];
    const rowDuplicateResult = findRowLevelDuplicates(liveData);

    if (rowDuplicateResult.hasDuplicates) {
      toast({
        variant: 'error',
        title: 'Duplicate entry detected',
        description: (
          <Flex direction="column">
            {rowDuplicateResult.duplicateRows.map(issue => (
              <Text
                key={`${issue.rowIndex}-${issue.value}-${issue.fieldKeys.join(
                  '-'
                )}`}
              >
                {buildRowDuplicateMessage(issue)}
              </Text>
            ))}
          </Flex>
        ),
        isClosable: true
      });
      return;
    }

    const response = await updatedMutation.mutateAsync({
      payload: buildResearchFocusPayload(values, userDetails)
    });
    const message =
      response?.message?.details?.[0]?.message ??
      response?.message?.message ??
      response?.message ??
      'Internal Server Error';

    toast({
      variant: response.isSuccess ? 'success' : 'error',
      title: response.isSuccess ? 'Success' : 'Error',
      description: message,
      isClosable: true
    });

    if (response.isSuccess) {
      await onSaved?.();
      onClose();
    }
  };

  if (!isOpen) return null;

  const isLoading = isProfileLoading || isSchemaLoading;
  const hasRequiredConfigs = Boolean(configs?.Specialty && configs?.Keyword);

  return (
    <Overlay overflow="hidden" backgroundColor="surfaceSecondaryFill">
      <Layout width="100%" paddingY={0} backgroundColor="surfaceSecondaryFill">
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleSave)}>
            <Flex direction="column" minHeight="100vh">
              <Flex direction="column" flex={1} overflowY="auto" px={4} pt={3}>
                <Flex alignItems="center" gap={2} mb={3}>
                  <FontAwesomeIcon icon={faEdit} />
                  <Text textStyle="headingGroup">
                    Edit Research Specialty & Keyword(s)
                  </Text>
                </Flex>

                {isLoading ? (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    minHeight="220px"
                  >
                    <Spinner />
                  </Flex>
                ) : hasRequiredConfigs ? (
                  <OutlineCard width="100%" padding={3}>
                    <SpecialtyKeywordTable
                      configs={{
                        Specialty: configs!.Specialty!,
                        SubSpecialty: configs!.SubSpecialty,
                        Keyword: configs!.Keyword!,
                        ScandinavianKeyword: configs!.ScandinavianKeyword
                      }}
                    />
                  </OutlineCard>
                ) : (
                  <Text textStyle="textBody">
                    Scandinavian keyword form schema is unavailable.
                  </Text>
                )}
              </Flex>

              <Box
                position="sticky"
                bottom={0}
                padding={4}
                bgColor="surfacePrimaryFill"
                boxShadow="elevation2"
              >
                <Flex justifyContent="space-between">
                  <Button variant="caution" onClick={onClose}>
                    Exit Without Saving
                  </Button>
                  <Button
                    type="submit"
                    disabled={!hasRequiredConfigs || updatedMutation.isLoading}
                    isDisabled={
                      !hasRequiredConfigs || updatedMutation.isLoading
                    }
                  >
                    {updatedMutation.isLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </form>
        </FormProvider>
      </Layout>
    </Overlay>
  );
};

export default ScandinavianKeywordProfileOverlay;
